import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { toast } from 'react-toastify';
import Dashboard from './Dashboard';
import './admin.css';

const ProjectManager = () => {
  const [movies, setMovies] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMovie, setEditingMovie] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    releaseDate: '',
    year: '',
    status: 'released',
    trailerLink: '',
    imageFile: null,
    imageUrl: ''
  });

  useEffect(() => {
    fetchMovies();
  }, []);

  const fetchMovies = async () => {
    try {
      const moviesQuery = query(collection(db, 'movies'), orderBy('createdAt', 'desc'));
      const moviesSnapshot = await getDocs(moviesQuery);
      const moviesList = moviesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMovies(moviesList);
    } catch (error) {
      console.error('Error fetching movies:', error);
      toast.error('Failed to load movies');
    }
  };

  const uploadImage = async (file) => {
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    formDataUpload.append('project', 'mms');
    formDataUpload.append('type', 'movies');

    const response = await fetch('https://imageapi.magicmoonstudios.com/upload.php', {
      method: 'POST',
      body: formDataUpload
    });

    const result = await response.json();
    
    if (result.success && result.url) {
      return result.url;
    } else {
      throw new Error(result.error || 'Upload failed');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, imageFile: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.imageFile && !formData.imageUrl) {
      toast.error('Please select an image');
      return;
    }

    if (!formData.title || !formData.category) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!formData.releaseDate && !formData.year) {
      toast.error('Please provide either a release date or year');
      return;
    }

    setUploading(true);

    try {
      let imageUrl = formData.imageUrl;

      if (formData.imageFile) {
        imageUrl = await uploadImage(formData.imageFile);
      }

      const movieData = {
        title: formData.title,
        category: formData.category,
        releaseDate: formData.releaseDate || '',
        year: formData.year || '',
        status: formData.status,
        trailerLink: formData.trailerLink || '',
        imageUrl: imageUrl,
        slug: formData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
        updatedAt: serverTimestamp()
      };

      if (editingMovie) {
        await updateDoc(doc(db, 'movies', editingMovie.id), movieData);
        toast.success('Movie updated successfully!');
      } else {
        movieData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'movies'), movieData);
        toast.success('Movie added successfully!');
      }

      setFormData({
        title: '',
        category: '',
        releaseDate: '',
        year: '',
        status: 'released',
        trailerLink: '',
        imageFile: null,
        imageUrl: ''
      });
      setShowAddModal(false);
      setEditingMovie(null);
      fetchMovies();
    } catch (error) {
      console.error('Error saving movie:', error);
      toast.error('Failed to save movie: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (movie) => {
    setEditingMovie(movie);
    setFormData({
      title: movie.title,
      category: movie.category,
      releaseDate: movie.releaseDate || '',
      year: movie.year || '',
      status: movie.status,
      trailerLink: movie.trailerLink || '',
      imageFile: null,
      imageUrl: movie.imageUrl
    });
    setShowAddModal(true);
  };

  const handleDelete = async (movieId, imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this movie?')) return;

    try {
      await deleteDoc(doc(db, 'movies', movieId));

      // Delete image from server
      try {
        await fetch('https://imageapi.magicmoonstudios.com/delete.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageUrl })
        });
      } catch (error) {
        console.error('Error deleting image:', error);
      }

      toast.success('Movie deleted successfully!');
      fetchMovies();
    } catch (error) {
      console.error('Error deleting movie:', error);
      toast.error('Failed to delete movie');
    }
  };

  return (
    <Dashboard>
      <div className="project-manager">
        <div className="banner-header">
          <h1>Project Manager</h1>
          <button className="add-banner-btn" onClick={() => setShowAddModal(true)}>
            + Add New Movie
          </button>
        </div>

        <div className="info-text">
          Manage your movie projects. Add details, upload posters, and control what appears on the homepage.
        </div>

        {movies.length === 0 ? (
          <div className="no-banners">
            <p>No movies added yet</p>
            <p className="note">Click "Add New Movie" to get started</p>
          </div>
        ) : (
          <div className="banners-grid">
            {movies.map((movie) => (
              <div key={movie.id} className="banner-item">
                <div className="banner-preview">
                  <img src={movie.imageUrl} alt={movie.title} style={{ width: '100%', height: 'auto', objectFit: 'contain' }} />
                  <div className="banner-overlay">
                    <span className="media-type-badge">{movie.status}</span>
                    <span className="priority-badge">{movie.year || movie.releaseDate}</span>
                  </div>
                </div>
                <div className="banner-details">
                  <h3>{movie.title}</h3>
                  <p className="subtitle">{movie.category}</p>
                  {movie.trailerLink && (
                    <p className="button-info">
                      🎬 Trailer: {movie.trailerLink}
                    </p>
                  )}
                  <div className="banner-actions" style={{ marginTop: '15px' }}>
                    <button className="edit-btn" onClick={() => handleEdit(movie)}>
                      Edit
                    </button>
                    <button className="delete-btn" onClick={() => handleDelete(movie.id, movie.imageUrl)}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showAddModal && (
          <div className="modal-overlay" onClick={() => { setShowAddModal(false); setEditingMovie(null); }}>
            <div className="modal-content banner-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingMovie ? 'Edit Movie' : 'Add New Movie'}</h2>
                <button className="close-btn" onClick={() => { setShowAddModal(false); setEditingMovie(null); }}>✕</button>
              </div>

              <form onSubmit={handleSubmit} className="banner-form">
                <div className="form-group">
                  <label>Movie Title *</label>
                  <input
                    type="text"
                    placeholder="e.g., Masth Kalandar"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Category/Genre *</label>
                  <input
                    type="text"
                    placeholder="e.g., Love, Horror, Action"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Release Date (Optional)</label>
                  <input
                    type="date"
                    value={formData.releaseDate}
                    onChange={(e) => setFormData({ ...formData, releaseDate: e.target.value })}
                  />
                  <p className="help-text">Leave empty if you only have the year</p>
                </div>

                <div className="form-group">
                  <label>Release Year {!formData.releaseDate ? '*' : '(Optional)'}</label>
                  <input
                    type="text"
                    placeholder="e.g., 2023"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    required={!formData.releaseDate}
                  />
                  <p className="help-text">Required if no release date is provided</p>
                </div>

                <div className="form-group">
                  <label>Status *</label>
                  <div className="radio-group">
                    <label>
                      <input
                        type="radio"
                        name="status"
                        value="released"
                        checked={formData.status === 'released'}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      />
                      Released
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="status"
                        value="upcoming"
                        checked={formData.status === 'upcoming'}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      />
                      Upcoming
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>Trailer Link (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g., https://www.youtube.com/watch?v=..."
                    value={formData.trailerLink}
                    onChange={(e) => setFormData({ ...formData, trailerLink: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Movie Poster *</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  {editingMovie && !formData.imageFile && (
                    <p className="current-media">Current: {formData.imageUrl.split('/').pop()}</p>
                  )}
                </div>

                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={() => { setShowAddModal(false); setEditingMovie(null); }}>
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn" disabled={uploading}>
                    {uploading ? 'Saving...' : (editingMovie ? 'Update Movie' : 'Add Movie')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Dashboard>
  );
};

export default ProjectManager;
